import React, { createContext, useCallback, useContext, useLayoutEffect, useMemo, useState } from 'react';
import { CustomProvider } from 'rsuite';

// App-wide light/dark theme, as in the other Connect apps. rsuite's CustomProvider puts
// `rs-theme-<theme>` on <body>, which drives every rsuite component and the tokens in
// theme-dark.css; the shared NSI components follow `nsi-theme-dark` on <body> and Bootstrap
// follows `data-bs-theme` on <html>, both set below.
//
// The choice is kept in this browser only.

const ThemeContext = createContext({
  theme: 'light',
  isDark: false,
  toggleTheme: () => {},
  setTheme: () => {},
});

export const useTheme = () => useContext(ThemeContext);

const STORAGE_KEY = 'ems.theme';
const VALID_THEMES = new Set(['light', 'dark']);

const readStoredTheme = () => {
  try {
    const value = window.localStorage.getItem(STORAGE_KEY);
    return VALID_THEMES.has(value) ? value : null;
  } catch {
    return null;
  }
};

const writeStoredTheme = (theme) => {
  try {
    window.localStorage.setItem(STORAGE_KEY, theme);
  } catch {
    // ignore storage failures (private mode, quota, etc.)
  }
};

export default function ThemeProvider({ children }) {
  const [theme, setThemeState] = useState(() => readStoredTheme() || 'light');

  const setTheme = useCallback((next) => {
    if (!VALID_THEMES.has(next)) return;
    setThemeState(next);
    writeStoredTheme(next);
  }, []);

  // Before paint, so a dark page doesn't flash light. rsuite's own class arrives with
  // CustomProvider's effect; the same class is set here so the tokens apply at once.
  useLayoutEffect(() => {
    const dark = theme === 'dark';
    document.body.classList.toggle('nsi-theme-dark', dark);
    document.body.classList.toggle('rs-theme-dark', dark);
    document.documentElement.setAttribute('data-bs-theme', dark ? 'dark' : 'light');
  }, [theme]);

  const toggleTheme = useCallback(() => {
    setTheme(theme === 'dark' ? 'light' : 'dark');
  }, [theme, setTheme]);

  const value = useMemo(
    () => ({ theme, isDark: theme === 'dark', toggleTheme, setTheme }),
    [theme, toggleTheme, setTheme]
  );

  return (
    <ThemeContext.Provider value={value}>
      <CustomProvider theme={theme}>{children}</CustomProvider>
    </ThemeContext.Provider>
  );
}
